import clsx from 'clsx';

// Lib
import {
    FACEBOOK,
    INSTAGRAM,
    LINKEDIN,
    PINTEREST,
    TWITTER,
    TIKTOK_BUSINESS,
    YOUTUBE
} from '@/lib/socialProfile';

// Utils
import { unifyTiktokNetworkTypes } from '@/utils/analytics/getAnalyticsGroupUsedNetworkTypes';

// Components
import Icon from '@/components/base/Icon';
import Instagram from '@/components/icons/Instagram';
import { YoutubeIcon } from '@/components/icons/Networks/YoutubeIcon';
import { PinterestIcon } from '@/components/icons/Networks/PinterestIcon';

// Types
import type { ComponentPropsWithoutRef } from 'react';
import type { SocialProfileNetworkType } from '@/types/socialProfileType';

type SocialNetworkTypeUsedProps = {
    socialNetworkType: SocialProfileNetworkType;
    isExport: NoDataNetworksListProps['isExport'];
};

// Render social network icons based on type
const SocialNetworkIcon = ({ socialNetworkType, isExport }: SocialNetworkTypeUsedProps) => {
    switch (socialNetworkType) {
        case INSTAGRAM:
            return <Instagram size={20} aria-label="instagram" forceLightMode={isExport} />;
        case PINTEREST:
            return <PinterestIcon size={20} aria-label="pinterest" forceLightMode={isExport} />;
        case YOUTUBE:
            return <YoutubeIcon size={20} aria-label="youtube" forceLightMode={isExport} />;
        default:
            return (
                <Icon
                    icon={socialNetworkType}
                    className={clsx({
                        'text-facebook': socialNetworkType === FACEBOOK,
                        'text-linkedin': socialNetworkType === LINKEDIN,
                        'text-twitter': socialNetworkType === TWITTER,
                        'text-tiktok': socialNetworkType === TIKTOK_BUSINESS,
                        'dark:text-white': !isExport
                    })}
                />
            );
    }
};

const SocialNetworkTypeUsed = ({ socialNetworkType, isExport }: SocialNetworkTypeUsedProps) => (
    <div
        className={clsx('flex h-15 w-15 items-center justify-center rounded-full bg-icoGray-300', {
            'dark:bg-icoDarkMode-deepBlack': !isExport
        })}>
        <SocialNetworkIcon socialNetworkType={socialNetworkType} isExport={isExport} />
    </div>
);

type NoDataNetworksListProps = ComponentPropsWithoutRef<'div'> & {
    socialNetworkTypes: SocialProfileNetworkType[];
    isExport: boolean;
};

const NoDataNetworksList = ({
    socialNetworkTypes,
    isExport,
    className,
    children
}: NoDataNetworksListProps) => (
    <div
        className={clsx(
            'flex w-full flex-1 flex-col items-center justify-center space-y-5 text-center',
            className
        )}>
        <div className="flex flex-row -space-x-4">
            {unifyTiktokNetworkTypes(socialNetworkTypes).map((socialNetworkType) => (
                <SocialNetworkTypeUsed
                    key={socialNetworkType}
                    socialNetworkType={socialNetworkType}
                    isExport={isExport}
                />
            ))}
        </div>
        <div data-testid="no-data" className="text-xs text-icoGray-500 dark:text-icoDarkMode-wolf">
            {children}
        </div>
    </div>
);

export default NoDataNetworksList;
